import Loading from '@/loading';
import type { Viewport } from '@xyflow/react';
import {
  Background,
  BackgroundVariant,
  NodeTypes,
  ReactFlow,
  ReactFlowProvider,
  SelectionMode,
} from '@xyflow/react';
import type { FC } from 'react';
import { memo, useEffect, useRef } from 'react';
import { CUSTOM_NODE } from './const';
import { WorkflowContextProvider } from './context';
import CustomEdge from './edges';
import { useNodesInteractions } from './hooks/use-nodes-interactions';
import { useWorkflowInit } from './hooks/use-workflow';
import { CustomNode } from './nodes';
import Operator from './operator';
import type { Edge, Node } from './types';

import '@xyflow/react/dist/base.css';
// import '@xyflow/react/dist/style.css';
import { cn } from '@/utils';
import { useDocumentVisibility } from 'ahooks';
import { setAutoFreeze } from 'immer';
import Header from './header';
import { useEdgesInteractions } from './hooks/use-edges-interactions';
import { useSelectionInteractions } from './hooks/use-selection-interactions';
import { useSyncDraft } from './hooks/use-sync-draft';
import Panel from './panel';
import './style.css';

setAutoFreeze(false);

const nodeTypes: NodeTypes = {
  [CUSTOM_NODE]: CustomNode,
};
const edgeTypes = {
  [CUSTOM_NODE]: CustomEdge,
};

type WorkflowProps = {
  nodes: Node[];
  edges: Edge[];
  viewport?: Viewport;
};
const Workflow: FC<WorkflowProps> = memo(({ nodes, edges, viewport }) => {
  const workflowContainerRef = useRef<HTMLDivElement>(null);

  const {
    onNodesChange,
    handleNodeClick,
    handleNodeConnectStart,
    handleNodeConnect,
    handleNodeConnectEnd,
    handleNodesDelete,
    handleNodeEnter,
    handleNodeLeave,
    handleNodeDragStop,
  } = useNodesInteractions();

  const { handleEdgesChange, handleEdgeEnter, handleEdgeLeave } =
    useEdgesInteractions();

  const { doSyncWorkflowDraft } = useSyncDraft();

  const { handleSelectionStart, handleSelectionChange, handleSelectionDrag } =
    useSelectionInteractions();

  const documentVisibility = useDocumentVisibility();

  useEffect(() => {
    if (documentVisibility !== 'hidden') return;
    doSyncWorkflowDraft();
  }, [documentVisibility, doSyncWorkflowDraft]);

  // useUnmount(doSyncWorkflowDraft);

  return (
    <div
      id="workflow-container"
      className={cn('relative w-full min-w-[960px] h-full bg-[#F0F2F7]')}
      ref={workflowContainerRef}
    >
      <Header />

      <Operator />

      <Panel />

      {/* <div
        tabIndex={-1}
        className={cn(
          'absolute inset-y-4 left-0 flex z-10 outline-none bg-white w-[500px] p-4 max-h-[calc(100vh-100px)] overflow-y-auto ',
        )}
      >
        <TableForm />
      </div> */}

      <ReactFlow
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodes={nodes}
        edges={edges}
        nodesDraggable={true}
        nodesConnectable={false}
        nodesFocusable={false}
        onNodesChange={onNodesChange}
        // onNodeDragStart={handleNodeDragStart}
        // onNodeDrag={handleNodeDrag}
        onNodeDragStop={handleNodeDragStop}
        onNodeClick={handleNodeClick}
        onNodeMouseEnter={handleNodeEnter}
        onNodeMouseLeave={handleNodeLeave}
        onNodesDelete={handleNodesDelete}
        onEdgesChange={handleEdgesChange}
        onEdgeMouseEnter={handleEdgeEnter}
        onEdgeMouseLeave={handleEdgeLeave}
        onConnect={handleNodeConnect}
        onConnectStart={handleNodeConnectStart}
        onConnectEnd={handleNodeConnectEnd}
        onSelectionStart={handleSelectionStart}
        onSelectionChange={handleSelectionChange}
        onSelectionDrag={handleSelectionDrag}
        selectionMode={SelectionMode.Partial}
        selectionOnDrag={false}
        draggable={false}
        defaultViewport={viewport}
        minZoom={0.25}
        viewport={viewport}
        // proOptions={{
        //   hideAttribution: true,
        // }}
      >
        <Background
          gap={[14, 14]}
          size={2}
          color="#E4E5E7"
          variant={BackgroundVariant.Dots}
        />
      </ReactFlow>
    </div>
  );
});
Workflow.displayName = 'Workflow';

const WorkflowWrap = memo(
  ({
    className,
    template_code,
    readonly,
  }: {
    className?: string;
    template_code: string;
    readonly: boolean;
  }) => {
    const { data, isLoading } = useWorkflowInit({
      template_code: template_code,
      readonly: readonly,
    });

    if (!data || isLoading) {
      return (
        <div className="relative flex justify-center items-center bg-[#F0F2F7] w-full h-full">
          <Loading />
        </div>
      );
    }

    return (
      <ReactFlowProvider>
        <Workflow
          nodes={data.nodes || []}
          edges={data.edges || []}
          // viewport={data?.graph.viewport}
        />
      </ReactFlowProvider>
    );
  },
);
WorkflowWrap.displayName = 'WorkflowWrap';

const WorkflowContainer: FC<{
  className?: string;
  template_code: string;
  readonly: boolean;
}> = ({ className, template_code, readonly }) => {
  return (
    <WorkflowContextProvider>
      <div className={className}>
        <WorkflowWrap readonly={readonly} template_code={template_code} />
      </div>
    </WorkflowContextProvider>
  );
};

export default memo(WorkflowContainer);
